import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageSquareText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchBeneficial, formatApiError } from "@/lib/api";

export default function BeneficialDetail() {
    const { slug } = useParams();
    const [b, setB] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchBeneficial(slug).then(setB).catch((e) => setError(formatApiError(e)));
    }, [slug]);

    if (error) {
        return (
            <div className="space-y-4" data-testid="beneficial-detail-error">
                <Button asChild variant="ghost" className="rounded-full">
                    <Link to="/beneficials"><ArrowLeft className="w-4 h-4 mr-2" /> Back to beneficials</Link>
                </Button>
                <p className="text-[var(--vm-text-muted)]">{error}</p>
            </div>
        );
    }

    if (!b) {
        return <p className="text-[var(--vm-text-muted)]" data-testid="beneficial-detail-loading">Loading…</p>;
    }

    const chatPrompt = encodeURIComponent(
        `How can I attract more ${b.name.toLowerCase()} to my garden in Ireland?`
    );

    return (
        <div className="space-y-10 max-w-3xl" data-testid={`beneficial-detail-${b.slug}`}>
            <Button asChild variant="ghost" className="rounded-full -ml-2">
                <Link to="/beneficials"><ArrowLeft className="w-4 h-4 mr-2" /> Back to beneficials</Link>
            </Button>

            {b.photo?.url && (
                <div
                    className="journal-card overflow-hidden"
                    style={{ background: b.photo.avg_color }}
                    data-testid="beneficial-hero-photo"
                >
                    <img
                        src={b.photo.url}
                        alt={b.photo.alt || b.name}
                        className="w-full h-72 object-cover"
                        loading="eager"
                    />
                    <div className="px-5 py-2 text-[11px] text-[var(--vm-text-muted)] bg-[var(--vm-card)]">
                        Photo by{" "}
                        <a
                            href={b.photo.photographer_url}
                            target="_blank" rel="noreferrer"
                            className="text-[var(--vm-sage)] hover:underline"
                        >
                            {b.photo.photographer}
                        </a>{" "}
                        on{" "}
                        <a
                            href={b.photo.pexels_url}
                            target="_blank" rel="noreferrer"
                            className="text-[var(--vm-sage)] hover:underline"
                        >
                            Pexels
                        </a>
                    </div>
                </div>
            )}

            <div className="flex items-start gap-5">
                <div className="text-7xl" aria-hidden>{b.emoji}</div>
                <div className="flex-1">
                    <p className="text-xs uppercase tracking-wider text-[var(--vm-sage)] mb-1">{b.role}</p>
                    <h1 className="font-serif-display text-5xl text-[var(--vm-text)]" data-testid="beneficial-detail-name">{b.name}</h1>
                </div>
            </div>

            {b.notes && (
                <section className="journal-card p-6">
                    <p className="text-sm text-[var(--vm-text-secondary)] leading-relaxed italic">{b.notes}</p>
                </section>
            )}

            {b.preys_on && b.preys_on.length > 0 && (
                <section className="journal-card p-6">
                    <h2 className="font-serif-display text-xl text-[var(--vm-text)] mb-2">Preys on</h2>
                    <div className="flex flex-wrap gap-2">
                        {b.preys_on.map((p) => (
                            <span key={p} className="text-xs px-3 py-1 rounded-full bg-[var(--vm-sage)]/15 text-[var(--vm-sage)] border border-[var(--vm-sage)]/40">{p}</span>
                        ))}
                    </div>
                </section>
            )}

            <section className="journal-card p-6" data-testid="beneficial-how-to-attract">
                <h2 className="font-serif-display text-xl text-[var(--vm-text)] mb-3">How to encourage them</h2>
                <ol className="space-y-2 list-decimal ml-5">
                    {(b.how_to_attract || []).map((c) => (
                        <li key={c} className="text-sm text-[var(--vm-text-secondary)] leading-relaxed">{c}</li>
                    ))}
                </ol>
            </section>

            <section className="journal-card p-6">
                <h2 className="font-serif-display text-xl text-[var(--vm-text)] mb-2">Life cycle</h2>
                <p className="text-sm text-[var(--vm-text-secondary)] leading-relaxed">{b.life_cycle}</p>
            </section>

            <div className="journal-card p-5 flex items-center gap-4">
                <MessageSquareText className="w-6 h-6 text-[var(--vm-sage)] shrink-0" />
                <div className="flex-1">
                    <h3 className="font-serif-display text-xl text-[var(--vm-text)]">Want a plan for your garden?</h3>
                    <p className="text-sm text-[var(--vm-text-muted)]">Ask rooteed how to attract {b.name} to your patch.</p>
                </div>
                <Button asChild className="rounded-full bg-[var(--vm-sage)] hover:opacity-90 text-[var(--vm-bg)]">
                    <Link to={`/chat?prompt=${chatPrompt}`} data-testid="beneficial-ask-ai-btn">Ask rooteed</Link>
                </Button>
            </div>
        </div>
    );
}
